import { createFileRoute } from '@tanstack/react-router'
import type { AssessmentFilters } from '@/features/assessments/components/AssessmentsTable'
import { BulkAssessDirectReportsButton } from '@/features/assessments'
import { AssessmentsTable } from '@/features/assessments/components/AssessmentsTable'
import { useMyConvexUserId } from '@/features/auth'
import { useAssessmentsFilterStore } from '@/features/assessments/stores/assessmentsFilterStore'
import { useDirectReportAssessments } from '@/features/assessments/services/assessments.convex'
import { PeriodConfigRequiredBanner } from '@/features/organization'

export const Route = createFileRoute(
  '/(dashboard)/assessments/direct-reports/',
)({
  component: DirectReportsAssessmentsPage,
  validateSearch: () => ({}), // Clear any search params from previous routes
  beforeLoad: () => ({
    breadcrumbs: [
      { title: 'Assessments', href: '/assessments' },
      { title: `Direct Reports` },
    ],
  }),
})

function DirectReportsAssessmentsPage() {
  const convexUserId = useMyConvexUserId()
  const filters = useAssessmentsFilterStore((state) => state.filters)
  const setFilters = useAssessmentsFilterStore((state) => state.setFilters)

  // Convert date strings to unix timestamps for Convex
  const convexFilters = {
    status: filters.status,
    dateFrom: filters.dateFrom
      ? new Date(filters.dateFrom).getTime()
      : undefined,
    dateTo: filters.dateTo ? new Date(filters.dateTo).getTime() : undefined,
  }

  const { results, status, loadMore } =
    useDirectReportAssessments(convexFilters)

  const isLoading = status === 'LoadingFirstPage'
  const hasMore = status === 'CanLoadMore'
  const isLoadingMore = status === 'LoadingMore'

  const handleFilterChange = (newFilters: AssessmentFilters) => {
    setFilters(newFilters)
  }

  return (
    <div className="space-y-6">
      <PeriodConfigRequiredBanner />
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Direct Report Assessments
          </h2>
          <p className="text-sm text-muted-foreground">
            View and manage assessments for your direct reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BulkAssessDirectReportsButton />
        </div>
      </div>

      <AssessmentsTable
        assessments={results}
        currentUserId={convexUserId ?? ''}
        isLoading={isLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={() => loadMore(10)}
        onFilterChange={handleFilterChange}
        isDirectReportsView={true}
      />
    </div>
  )
}
